import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { catchError, map, mergeMap, of } from 'rxjs'
import { ActivityService } from 'src/app/services/activity/activity.service'
import {
  fetch_activity_dashboard_data,
  fetch_activity_dashboard_data_success,
  FETCH_ACTIVITY_DETAILS,
  FETCH_ACTIVITY_DETAILS_SUCCESS,
  fetch_activity_types_data,
  fetch_activity_types_data_success,
  LOAD_ACTIVITY_SUMMARY_DETAILS,
  LOAD_ACTIVITY_SUMMARY_DETAILS_SUCCESS,
  save_activity_action,
  save_activity_success_action,
  UPDATE_ACTIVITY_ACTION,
  update_activity_pending_item,
  update_activity_pending_item_success,
  UPDATE_ACTIVITY_SUCCESS_ACTION
} from '../action/activity.action'
import {
  route_to_action,
  show_display_id_after_created_action,
  show_success_msg_after_updation_action,
  show_error_action
} from '../action/common/common.action'

@Injectable()
export class ActivityEffects {
  constructor(private actions$: Actions, private activityService: ActivityService) {}

  fetch_activity_dashboard_data$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetch_activity_dashboard_data),
      mergeMap(action =>
        this.activityService.callDashboardApi(action.days, action.sortBy, action.sortOrder).pipe(
          mergeMap(response => [fetch_activity_dashboard_data_success(response)]),
          catchError((error: any) => of(show_error_action({ data: null, error: error })))
        )
      )
    )
  )

  load_activity_summary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_ACTIVITY_SUMMARY_DETAILS),
      mergeMap(data =>
        this.activityService.invokeActivitySummaryAPI(data.queryParams, data.searchRequestBody).pipe(
          map(summaryResponse => LOAD_ACTIVITY_SUMMARY_DETAILS_SUCCESS(summaryResponse)),
          catchError((error: any) => of(show_error_action({ data: null, error: error })))
        )
      )
    )
  )

  save_activity_action$ = createEffect(() =>
    this.actions$.pipe(
      ofType(save_activity_action),
      mergeMap(({ activity, files, routeTo }) =>
        this.activityService.callSaveApi(activity, files).pipe(
          mergeMap(response => [
            save_activity_success_action({ activity: response, routeTo }),
            route_to_action({ routeTo }),
            show_display_id_after_created_action({ moduleName: 'Activity', displayId: response?.displayId })
          ]),
          catchError((error: any) => of(show_error_action({ data: null, error: error })))
        )
      )
    )
  )

  fetch_activity_details$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FETCH_ACTIVITY_DETAILS),
      mergeMap(({ id }) =>
        this.activityService.callActivityDetailsApi(id).pipe(
          mergeMap(activity => [FETCH_ACTIVITY_DETAILS_SUCCESS({ activity })]),
          catchError((error: any) => of(show_error_action({ data: null, error: error })))
        )
      )
    )
  )

  update_activity_details$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UPDATE_ACTIVITY_ACTION),
      mergeMap(({ activity, id, files, routeTo }) =>
        this.activityService.callUpdateApi(activity, id, files).pipe(
          mergeMap(activity => [
            UPDATE_ACTIVITY_SUCCESS_ACTION({ activity, routeTo }),
            route_to_action({ routeTo }),
            show_success_msg_after_updation_action({ moduleName: 'Activity' })
          ]),
          catchError((error: any) => of(show_error_action({ data: null, error: error })))
        )
      )
    )
  )

  bulk_pending_update_activity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(update_activity_pending_item),
      mergeMap((data: any) =>
        this.activityService.bulkUpdatePendingActivityAPI(data).pipe(
          mergeMap(activity => [
            update_activity_pending_item_success(data),
            show_success_msg_after_updation_action({
              moduleName: Object.entries(data?.data?.elementIdCompanyIdMap)?.length > 1 ? 'Activities' : 'Activity'
            })
          ]),
          catchError((error: any) => of(show_error_action({ data: null, error: error })))
        )
      )
    )
  )

  fetch_activity_types_data$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetch_activity_types_data),
      mergeMap(action =>
        this.activityService.callActivityType().pipe(
          mergeMap((response: any) => [fetch_activity_types_data_success({ types: response })]),
          catchError((error: any) => of(show_error_action({ data: null, error: error })))
        )
      )
    )
  )
}
